import axios from 'axios';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { mergeReportData } from '../utils/mergeReportData';
import {
  batchStart,
  batchProgress,
  batchComplete,
  batchError,
  batchAbort,
  batchCancel,
} from '../ducks/report';

// The fetch function signature: takes a batch key and an abort signal
export type BatchFetchFn = (batchKey: string, signal: AbortSignal) => Promise<IReportData>;

// Failures a second attempt cannot fix: the session has lapsed or the user lost access, so every
// retry returns the same status. Offering a re-run for these traps the user in a loop instead of
// letting them go and sign in again.
const NON_RETRYABLE_STATUSES = [401, 403];

// DOMException name for a fetch/AbortController cancellation.
const NATIVE_ABORT_ERROR_NAME = 'AbortError';

// Thrown when a request succeeds but the body is not a report — an SSO login page, a proxy's HTML
// error page, a renamed field. The transport worked, so there is no failing status to classify on;
// asking again returns the same body.
class ReportShapeError extends Error {
  constructor() {
    super('The server did not return report data');
    this.name = 'ReportShapeError';
  }
}

const isReportData = (value: unknown): value is IReportData =>
  !!value && typeof value === 'object' && Array.isArray((value as IReportData).rows);

// Deliberately not "no status means retryable". A status describes what the transport did and says
// nothing about whether the payload made sense, so a shape failure and a programming error both
// arrive without one — and both return the same result however many times they are retried.
// Only a real transport failure earns a retry: a 5xx, a timeout, a dropped connection.
const canRetryAfter = (err: unknown): boolean => {
  if (!axios.isAxiosError(err)) return false;

  const status = err.response?.status;
  return !status || !NON_RETRYABLE_STATUSES.includes(status);
};

interface UseBatchedReportOptions {
  batchKeys: string[]; // e.g. team IDs, or a single key for CLE
  fetchFn: BatchFetchFn;
}

interface UseBatchedReportResult {
  trigger: () => void;
  abort: () => void;
  cancel: () => void;
}

export const useBatchedReport = ({
  batchKeys,
  fetchFn,
}: UseBatchedReportOptions): UseBatchedReportResult => {
  const dispatch = useDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isAbortedRef = useRef(false);

  // Stable reference to the latest fetchFn and batchKeys
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;
  const batchKeysRef = useRef(batchKeys);
  batchKeysRef.current = batchKeys;

  const stopInFlight = useCallback(() => {
    isAbortedRef.current = true;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  // Teardown without flagging the run as interrupted: clearing the decks before a new trigger, and
  // CLEReport's Cancel button, which stays on this path because the not-loaded canvas is out of
  // scope there.
  //
  // Note the cleanup effect below does NOT call this. It aborts the controller and dispatches
  // batchAbort by hand, leaving isAbortedRef down, so the loop's aborted-checks do not trip and it
  // ends only because the fetch rejects — an assumption about BatchFetchFn that nothing enforces.
  // Which callers that exposes is the opposite of what you might guess: CLEReport calls abort() in
  // its own unmount cleanup and is therefore covered, while GeneralReport and TeamManagerReport
  // rely solely on the hook's cleanup and are not. Calling abort() from that cleanup would close
  // it; left alone by decision, not because it is hard.
  const abort = useCallback(() => {
    stopInFlight();
    dispatch(batchAbort());
  }, [stopInFlight, dispatch]);

  // The user pressing Cancel on GeneralReport/TeamManagerReport. Same teardown as abort, but
  // flagged so the report area shows the not-loaded canvas and the panel's Run button goes live.
  const cancel = useCallback(() => {
    stopInFlight();
    dispatch(batchCancel());
  }, [stopInFlight, dispatch]);

  const trigger = useCallback(async () => {
    const keys = batchKeysRef.current;
    const fn = fetchFnRef.current;

    if (keys.length === 0) return;

    // Abort any in-flight batch sequence
    abortControllerRef.current?.abort();
    isAbortedRef.current = false;

    dispatch(batchStart());

    let accumulatedRows: IReportRow[] = [];
    const totalBatches = keys.length;

    for (let i = 0; i < totalBatches; i++) {
      if (isAbortedRef.current) return;

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const result = await fn(keys[i], controller.signal);

        if (isAbortedRef.current) return;

        // Checked here rather than trusted from the `as IReportData` cast at the call sites: a cast
        // is a promise to the compiler, not a check. Without this the next line throws a raw
        // TypeError that carries no status, which the classifier would read as retryable.
        if (!isReportData(result)) throw new ReportShapeError();

        accumulatedRows = mergeReportData(accumulatedRows, result.rows);

        const progress = Math.round(((i + 1) / totalBatches) * 100);
        dispatch(batchProgress(progress));
      } catch (err) {
        if (isAbortedRef.current) return;

        // A cancellation is not a failure. api.ts bridges our AbortSignal to axios's CancelToken,
        // and axios 0.21.4's Cancel is a plain constructor — NOT an Error subclass — so only
        // axios.isCancel recognises it. An `instanceof Error` check silently misses every one and
        // reports "Cancel: Request aborted" to the user as a failed report.
        //
        // The native-abort arm below catches nothing today: axios 0.21.4 has no AbortSignal support
        // and nothing here uses fetch(). It is forward-compatibility for whenever that changes.
        const isNativeAbort = err instanceof Error && err.name === NATIVE_ABORT_ERROR_NAME;
        if (axios.isCancel(err) || isNativeAbort) return;

        // The only record this failure leaves. The user is shown a generic line by design, so
        // without this the status, the server's own message body and the failing team all go
        // nowhere and a support ticket saying "it says something went wrong" is unanswerable.
        // eslint-disable-next-line no-console
        console.error('Report batch failed', {
          batchKey: keys[i],
          batchIndex: i + 1,
          totalBatches,
          status: axios.isAxiosError(err) ? err.response?.status : undefined,
          responseBody: axios.isAxiosError(err) ? err.response?.data : undefined,
          error: err,
        });

        dispatch(
          batchError({
            message: err instanceof Error ? err.message : String(err),
            canRetry: canRetryAfter(err),
          }),
        );
        return;
      }
    }

    if (!isAbortedRef.current) {
      dispatch(batchComplete({ rows: accumulatedRows }));
    }
  }, [dispatch]);

  // Cleanup on unmount — abort network request and reset Redux state.
  //
  // Not `abort()`: this skips stopInFlight, so isAbortedRef stays down and the loop relies on the
  // fetch rejecting to end. See the note on abort above. Calling abort() here is a one-line change
  // and would close it; left out only because it is pre-existing and outside PA-3289. Do not read
  // this as needing the per-run id — that is a separate race, where a superseded run's callbacks
  // are indistinguishable from the current one's, and no change here fixes that.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      dispatch(batchAbort());
    };
  }, [dispatch]);

  return {
    trigger,
    abort,
    cancel,
  };
};
